import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, reactive, ref, watch } from 'vue';
import InfoBlock, { InformationBlock } from 'ah-common-lib/src/common/components/InfoBlock.vue';
import { FormDefinition } from 'ah-common-lib/src/form/interfaces';
import { setState, toDataModel, updateModel, getChildModel, resetForm } from 'ah-common-lib/src/form/helpers';
import { makeCompanyDetailsFM, makeCompanyAddressFM } from './reviewForms';
import { Client, EntityType } from 'ah-api-gateways';
import { useSettingsStore } from '@/app/store/settingsModule';
import { capitalize } from 'lodash';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { getServices } from '@/app/services';
import { useAuthStore } from '@/app/store/authModule';
import { catchError, mergeMap } from 'rxjs/operators';
import { waitForCQRSEntityChange } from 'ah-requests';
import { of } from 'rxjs';
import { useToast } from 'ah-common-lib/src/toast';


export default /*#__PURE__*/_defineComponent({
  __name: 'CompanyDetailsReview',
  props: {
    editable: { type: Boolean, default: true },
    model: null
  },
  emits: ["update:model"],
  setup(__props: any, { emit }: { emit: ({ (e: 'update:model', value: Client): void }), expose: any, slots: any, attrs: any }) {

const props = __props as { editable: boolean, model: Client };



const state = reactive<{ editing: boolean }>({ editing: false });



const settingsStore = useSettingsStore();

const requestManager = useRequestManager().manager;

const services = getServices();

const authStore = useAuthStore();

const toast = useToast();

settingsStore.loadCountries();
settingsStore.loadEntityTypes();

function countryToHuman(cc?: string) {
  if (!cc) return '-';
  return settingsStore.allCountries?.find((country) => country.cc === cc)?.name || '-';
}

function entityToHuman(entityType?: string) {
  if (!entityType) return '-';
  return (
    settingsStore.entityTypesList?.find((entity: EntityType) => entity.id === entityType)?.legalForm ||
    capitalize(entityType.toLowerCase().split('_').join(' '))
  );
}

const companyInfoBlock = computed<InformationBlock[]>(() => [
  {
    label: 'Country of Incorporation',
    key: 'countryCode',
    value: countryToHuman(props.model.companyDetails.address.countryCode),
    cols: 4,
  },
  { label: 'Company Type', key: 'entityType', value: entityToHuman(props.model.companyDetails.entityType), cols: 4 },
  { label: 'Registered Company Name', key: 'name', cols: 4 },
  { label: 'Company Number', key: 'registrationNumber', cols: 4 },
  { label: 'Sector', key: 'sectorDescription', cols: 4 },
  { label: 'Company Website', key: 'website', cols: 4 },
  { label: 'Company Telephone', key: 'phoneNumber', cols: 4 },
]);

const addressBlock = ref<InformationBlock[]>([
  { label: 'Street', key: 'addressLine', cols: 4 },
  { label: 'Postcode / ZIP Code', key: 'postalCode', cols: 4 },
  { label: 'City', key: 'city', cols: 4 },
  { label: 'State/Province/County', key: 'stateOrProvince', cols: 4 },
]);

const companyDetailsForm = reactive<FormDefinition>({
  form: makeCompanyDetailsFM(),
  validation: null,
});

const addressForm = reactive<FormDefinition>({
  form: makeCompanyAddressFM(),
  validation: null,
});

const tradingAddressForm = reactive<FormDefinition>({
  form: makeCompanyAddressFM(),
  validation: null,
});

const anyInvalid = computed(
  () =>
    companyDetailsForm.validation?.$invalid ||
    addressForm.validation?.$invalid ||
    tradingAddressForm.validation?.$invalid
);

const isEditable = computed(() => props.editable !== false);

const companyDetailsModel = computed(() => ({
  countryCode: props.model.companyDetails.address.countryCode,
  ...props.model.companyDetails,
  name: props.model.name,
}));

function save() {
  const clientId = authStore.loggedInIdentity!.client!.id;
  const { countryCode, ...companyDetails } = toDataModel(companyDetailsForm.form);
  const address = { ...toDataModel(addressForm.form), countryCode };
  const tradingAddress = { ...toDataModel(tradingAddressForm.form), countryCode };

  Object.keys(companyDetails).forEach((k) => {
    if (companyDetails[k] === '') {
      delete companyDetails[k];
    }
  });

  const model = {
    name: companyDetails.name,
    companyDetails: {
      ...companyDetails,
      address,
      tradingAddress,
    },
  };

  requestManager
    .currentOrNew(
      'saveClient',
      services.registration
        .updateClient(clientId, model)
        .pipe(
          mergeMap((idEntity) =>
            waitForCQRSEntityChange(idEntity, () => services.client.getClient(idEntity.id)).pipe(
              catchError(() => of(props.model))
            )
          )
        )
    )
    .subscribe((client) => {
      toast?.success('Company details changed successfully');
      state.editing = false;
      emit('update:model', client);
    });
}

function toggleEditing() {
  state.editing = !state.editing;
  if (!state.editing) {
    resetForms(true);
  }
}

onBeforeMount(() => {
  settingsStore.loadEntityTypes(true).then((types) =>
    setState(
      getChildModel(companyDetailsForm.form, 'entityType')!,
      'options',
      types.map((c) => ({ value: c.id, label: c.legalForm }))
    )
  );

  settingsStore.loadCountries(true).then((countries) =>
    setState(
      getChildModel(companyDetailsForm.form, 'countryCode')!,
      'options',
      countries.map((c) => ({ value: c.cc, label: c.name }))
    )
  );
});

function resetForms(resetState = false) {
  updateModel(companyDetailsForm.form, {
    countryCode: props.model.companyDetails.address.countryCode,
    ...props.model,
    ...props.model.companyDetails,
  });
  updateModel(addressForm.form, props.model.companyDetails.address ?? {});
  updateModel(tradingAddressForm.form, props.model.companyDetails.tradingAddress ?? {});

  if (resetState) {
    companyDetailsForm.validation && resetForm(companyDetailsForm.validation);
    addressForm.validation && resetForm(addressForm.validation);
    tradingAddressForm.validation && resetForm(tradingAddressForm.validation);
  }
}

watch(
  () => props.model,
  () => resetForms(),
  { immediate: true }
);

return { __sfc: true,props, state, emit, settingsStore, requestManager, services, authStore, toast, countryToHuman, entityToHuman, companyInfoBlock, addressBlock, companyDetailsForm, addressForm, tradingAddressForm, anyInvalid, isEditable, companyDetailsModel, save, toggleEditing, resetForms, InfoBlock }
}

})